const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-api'
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'

interface Grecaptcha {
  ready: (callback: () => void) => void
  render: (
    container: HTMLElement,
    parameters: {
      'sitekey': string
      'size'?: 'invisible' | 'compact' | 'normal'
      'callback'?: (token: string) => void
      'error-callback'?: () => void
      'expired-callback'?: () => void
    },
  ) => number
  execute: (widgetId: number) => void
  reset: (widgetId?: number) => void
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha
  }
}

function getSiteKey(): string {
  return import.meta.env.VITE_RECAPTCHA_KEY?.trim() ?? ''
}

export function isRecaptchaEnabled(): boolean {
  return getSiteKey().length > 0
}

function loadRecaptchaScript(): Promise<void> {
  if (window.grecaptcha) {
    return new Promise((resolve) => {
      window.grecaptcha!.ready(() => resolve())
    })
  }

  const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => {
        window.grecaptcha?.ready(() => resolve())
      }, { once: true })
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')), { once: true })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = RECAPTCHA_SCRIPT_ID
    script.src = RECAPTCHA_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => {
      window.grecaptcha?.ready(() => resolve())
    }, { once: true })
    script.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')), { once: true })
    document.head.appendChild(script)
  })
}

export function useRecaptcha(containerEl: Ref<HTMLElement | null>) {
  const widgetId = ref<number | null>(null)
  const enabled = computed(() => isRecaptchaEnabled())

  let tokenResolver: ((token: string) => void) | null = null
  let tokenRejecter: ((error: Error) => void) | null = null

  async function mountWidget() {
    if (!enabled.value || !containerEl.value || widgetId.value !== null)
      return

    await loadRecaptchaScript()
    if (!window.grecaptcha || !containerEl.value)
      return

    widgetId.value = window.grecaptcha.render(containerEl.value, {
      'callback': (token: string) => {
        tokenResolver?.(token)
        tokenResolver = null
        tokenRejecter = null
      },
      'error-callback': () => {
        tokenRejecter?.(new Error('reCAPTCHA error'))
        tokenResolver = null
        tokenRejecter = null
      },
      'expired-callback': () => {
        window.grecaptcha?.reset(widgetId.value ?? undefined)
      },
      'sitekey': getSiteKey(),
      'size': 'invisible',
    })
  }

  async function execute(): Promise<string> {
    if (!enabled.value)
      return ''

    await mountWidget()
    if (widgetId.value === null || !window.grecaptcha)
      throw new Error('reCAPTCHA is not ready')

    return new Promise<string>((resolve, reject) => {
      tokenResolver = resolve
      tokenRejecter = reject
      window.grecaptcha!.reset(widgetId.value!)
      window.grecaptcha!.execute(widgetId.value!)
    })
  }

  function reset() {
    if (widgetId.value !== null)
      window.grecaptcha?.reset(widgetId.value)
  }

  watch(containerEl, (el) => {
    if (el)
      void mountWidget()
  })

  onMounted(() => {
    if (enabled.value)
      void mountWidget()
  })

  return {
    enabled,
    execute,
    reset,
  }
}
